import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { Resolver, FormProvider, useForm } from "react-hook-form"
import { Box, Stack, CircularProgress } from "@mui/material"

import { BulkLabelsReqBody, useCreateBulkLabels } from "@app/hooks/useCreateBulkLabels"
import { useAddGlobalToast } from "@app/providers/GlobalToastProvider"
import { useGetPermissions } from "@app/hooks/usePermissions"
import type { BulkPartPickerOption } from "@app/components/pickers/bulk-part-picker/BulkPartPicker"
import NoAccessPage from "@app/pages/NoAccess"
import { Generator } from "./Generator"
import { LabelView } from "./LabelView"

// Type for each item in the bulkPartSelection array
export type BulkPartOption = {
	count: number | string // Can be string from TextField, gets converted to number
	part: BulkPartPickerOption | null // Can be null when not selected
}

// Type for the entire form values
export type LabelGeneratorFormValues = {
	bulkPartSelection: BulkPartOption[]
}

export const LabelGenerator: React.FC = () => {
	const addToast = useAddGlobalToast()
	const { data: permissions, isLoading: isLoadingPermissions } = useGetPermissions()
    const canCreate = permissions?.can_create ?? true

	const schema = yup.object().shape({
		bulkPartSelection: yup
			.array()
			.of(
				yup.object().shape({
					part: yup.mixed<BulkPartPickerOption>().nullable().required(`Please choose a bulk part`),
					count: yup
						.number()
						.typeError("Please enter a number")
						.required(`Please add a count`)
						.min(1, "Quantity must be at least 1."),
				})
			)
			.required(`Please add at least one bulk part option`)
			.min(1, "Please add at least one bulk part option")
			.test({
				name: "unique-bulk-parts",
				message: "Duplicate bulk cat classes aren't allowed to be selected",
				test(list) {
					if (!Array.isArray(list)) return true
					// part id -> indices map
					const groups = new Map<number | string, number[]>()

					for (let i = 0; i < list.length; i++) {
						const partId = list[i]?.part?.value?.part_id
						if (partId == null) {
							continue // let `required` handle empties
						}

						const arr = groups.get(partId) || []
						arr.push(i)
						groups.set(partId, arr)
					}

					const errorIndices: number[] = []
					groups.forEach((indices) => {
						if (indices.length > 1) {
							errorIndices.push(...indices.slice(1)) // all but first are errors
						}
					})

					if (errorIndices.length == 0) {
						return true
					}

					// create a validation error for each duplicate part
					const errs = errorIndices.map((i) =>
						this.createError({
							path: `${this.path}[${i}].part`,
							message: "Duplicate bulk cat classes aren't allowed to be selected",
						})
					)

					throw new (yup as any).ValidationError(errs)
				},
			}),
	})

	const resolver = yupResolver(schema, { abortEarly: false }) as unknown as Resolver<LabelGeneratorFormValues>

	const useFormMethods = useForm<LabelGeneratorFormValues>({
		mode: `onChange`,
		criteriaMode: `all`,
		resolver,
		defaultValues: {
			bulkPartSelection: [
				{
					part: null,
					count: 1,
				},
			],
		},
	})

	const mutation = useCreateBulkLabels({
		onSuccess: () => {
			addToast({
				message: "Bulk labels generated successfully!",
				severity: "success",
			})
		},
	})

	const bulkLabels = mutation.data?.bulk_labels ?? []

	const handleGenerate = async (data: LabelGeneratorFormValues) => {
		const payload: BulkLabelsReqBody[] = data.bulkPartSelection
			.filter(
				(item): item is BulkPartOption & { part: NonNullable<BulkPartOption["part"]> } => item.part !== null
			)
			.map((bulkPartOption) => ({
				part_id: bulkPartOption.part.value.part_id,
				part_number: bulkPartOption.part.value.part_number,
				count:
					typeof bulkPartOption.count === "string"
						? parseInt(bulkPartOption.count, 10)
						: bulkPartOption.count,
				description: bulkPartOption.part.value.search,
			}))

		if (payload.length === 0) {
			addToast({
				message: "No valid bulk cat classes selected for label generation.",
				severity: "error",
			})
			return
		}

		await mutation.mutateAsync({ bulk: payload })

		useFormMethods.reset()
	}

	if (isLoadingPermissions) {
		return (
			<Box
				sx={{
					width: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "400px",
				}}
			>
				<CircularProgress />
			</Box>
		)
	}

	if (!canCreate) {
		return <NoAccessPage />
	}

	return (
		<FormProvider {...useFormMethods}>
			<form onSubmit={useFormMethods.handleSubmit(handleGenerate)}>
				<Box sx={{ width: "100%", p: 2, display: "flex", flexDirection: "column", minHeight: 0 }}>
					<Stack direction={{ xs: "column", md: "row" }} gap={2} sx={{ minHeight: 0 }}>
						<Box
							sx={{
								flex: { xs: "0 1 auto", md: "0 0 420px" },
								minWidth: { xs: 0, md: 300 },
								width: { xs: "100%", md: "auto" },
							}}
						>
							<Generator />
						</Box>
						<Box
							sx={{
								flex: { xs: "0 1 auto", md: "1 1 0" },
								minWidth: { xs: 0, md: 300 },
								overflow: "hidden",
								display: { xs: "block", md: "flex" },
								minHeight: 0,
								width: { xs: "100%", md: 0 },
							}}
						>
							<LabelView bulkLabels={bulkLabels} />
						</Box>
					</Stack>
				</Box>
			</form>
		</FormProvider>
	)
}
export default LabelGenerator
